import React from 'react';
import { priceLine } from '../../utils/priceLine';
import { HOME_URL } from '../../config';

/** A page on the public site (HOME_URL is www.hypetunes.com unless a local build points it at a local web app). */
export const site = (path = '') => `${String(HOME_URL || 'https://www.hypetunes.com/').replace(/\/$/, '')}${path}`;

// The HypeFlyer Print Shop's controls (2026-10-31): one look for the four
// studio sections (Images, Posts, Who Is, Press Kit) so they read as one
// counter. Inline styles, like every World panel (there is no design system in
// this repo); the tokens below are the shop's own.
export const T = {
  ink: '#f6f1ff',
  dim: 'rgba(246,241,255,0.62)',
  faint: 'rgba(246,241,255,0.38)',
  line: 'rgba(255,79,163,0.26)',
  lineSoft: 'rgba(255,255,255,0.09)',
  hot: '#ff4fa3',         // HypeFlyer magenta
  sun: '#ffd84d',         // flyer yellow
  ok: '#7dffb0',
  warn: '#ffc46b',
  bad: '#ff7a7a',
  field: 'rgba(255,255,255,0.06)',
  panel: 'rgba(14,9,18,0.95)',
  font: 'system-ui, -apple-system, Segoe UI, sans-serif',
};

export const H = ({ children, sub }) => (
  <div style={{ margin: '2px 0 10px' }}>
    <div style={{ color: T.ink, font: `700 15px ${T.font}`, letterSpacing: 0.2 }}>{children}</div>
    {sub && <div style={{ color: T.dim, font: `12.5px/1.4 ${T.font}`, marginTop: 3 }}>{sub}</div>}
  </div>
);

export const Label = ({ children, hint }) => (
  <div style={{ color: T.faint, font: `700 10.5px ${T.font}`, letterSpacing: 1.1, textTransform: 'uppercase', margin: '12px 0 6px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
    <span>{children}</span>{hint && <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>{hint}</span>}
  </div>
);

export const Row = ({ children, gap = 8, wrap = true, style }) => (
  <div style={{ display: 'flex', gap, flexWrap: wrap ? 'wrap' : 'nowrap', alignItems: 'center', ...style }}>{children}</div>
);

/** Segmented buttons: options [{ value, label, disabled?, title? }]. */
export function Seg({ value, options, onChange, small = false }) {
  return (
    <Row gap={6}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            disabled={!!o.disabled}
            title={o.title || ''}
            onClick={() => !o.disabled && onChange(o.value)}
            style={{
              flex: small ? '0 0 auto' : '1 1 0', minWidth: small ? 0 : 74, cursor: o.disabled ? 'not-allowed' : 'pointer',
              padding: small ? '6px 10px' : '9px 10px', borderRadius: 10, font: `600 ${small ? 12 : 13}px ${T.font}`,
              color: o.disabled ? T.faint : on ? '#160a12' : T.ink,
              background: on ? T.hot : T.field,
              border: `1px solid ${on ? T.hot : T.lineSoft}`, opacity: o.disabled ? 0.5 : 1,
            }}
          >{o.label}</button>
        );
      })}
    </Row>
  );
}

const fieldBase = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 10,
  border: `1px solid ${T.lineSoft}`, background: T.field, color: T.ink, outline: 'none',
  font: `14px ${T.font}`,
};

/** A dropdown: options [{ value, label }]. */
export const Select = ({ value, options, onChange, disabled }) => (
  <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ ...fieldBase, appearance: 'auto', cursor: 'pointer' }}>
    {options.map((o) => <option key={o.value} value={o.value} style={{ color: '#111' }}>{o.label}</option>)}
  </select>
);

/* ⚠ NEVER DISABLED (platform rule: block the SEND, not the box). Text fields
   stay typeable while something is generating; the buttons say no. */
export const TextArea = ({ value, onChange, placeholder, rows = 3, maxLength }) => (
  <textarea value={value} rows={rows} maxLength={maxLength} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    style={{ ...fieldBase, resize: 'vertical', lineHeight: 1.45, minHeight: rows * 22 }} />
);

export const Input = ({ value, onChange, placeholder, type = 'text', maxLength }) => (
  <input value={value} type={type} maxLength={maxLength} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={fieldBase} />
);

export function Btn({ children, onClick, kind = 'primary', disabled, busy, title, style, type = 'button' }) {
  const k = {
    primary: { background: T.hot, color: '#170910', border: `1px solid ${T.hot}` },
    sun: { background: T.sun, color: '#1b1503', border: `1px solid ${T.sun}` },
    ghost: { background: 'transparent', color: T.ink, border: `1px solid ${T.lineSoft}` },
    danger: { background: 'transparent', color: T.bad, border: `1px solid rgba(255,122,122,0.4)` },
  }[kind];
  const off = disabled || busy;
  return (
    <button type={type} title={title || ''} disabled={off} onClick={off ? undefined : onClick}
      style={{ ...k, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.55 : 1, padding: '9px 14px', borderRadius: 10, font: `700 13.5px ${T.font}`, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center', ...style }}>
      {busy && <Spinner size={13} />}{children}
    </button>
  );
}

export const Check = ({ checked, onChange, children, disabled }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: disabled ? T.faint : T.ink, font: `13.5px ${T.font}`, cursor: disabled ? 'not-allowed' : 'pointer' }}>
    <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: T.hot, width: 16, height: 16 }} />
    {children}
  </label>
);

/** A price, the platform's way: dollars big, credits beneath (utils/priceLine). `free` prints "Free". */
export function PriceTag({ price, free = false, prefix = '' }) {
  if (free) return <span style={{ color: T.ok, font: `700 14px ${T.font}` }}>Free</span>;
  const p = priceLine(price);
  if (!p) return null;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'right' }}>
      <span style={{ color: T.ink, font: `800 15px ${T.font}` }}>{prefix}{p.amount}</span>
      {p.note && <small style={{ color: T.faint, font: `11px ${T.font}` }}>{p.note}</small>}
    </span>
  );
}

export function Note({ tone = 'info', children, style }) {
  const c = { info: T.dim, ok: T.ok, warn: T.warn, error: T.bad }[tone] || T.dim;
  return <div style={{ color: c, font: `12.5px/1.45 ${T.font}`, margin: '8px 0', ...style }}>{children}</div>;
}

export const Spinner = ({ size = 16 }) => (
  <span style={{ width: size, height: size, borderRadius: '50%', border: `2px solid rgba(255,255,255,0.25)`, borderTopColor: T.hot, display: 'inline-block', animation: 'htps-spin 0.8s linear infinite', flex: '0 0 auto' }}>
    <style>{'@keyframes htps-spin{to{transform:rotate(360deg)}}'}</style>
  </span>
);

/** A grid of thumbnails to pick from: images [{ key, url, label?, badge? }]. */
export function Thumbs({ images, selected, onPick, size = 72 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${size}px, 1fr))`, gap: 6 }}>
      {images.map((im) => (
        <button key={im.key} type="button" onClick={() => onPick(im.key)} title={im.label || ''}
          style={{ position: 'relative', padding: 0, aspectRatio: '1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#000', border: `2px solid ${im.key === selected ? T.hot : 'transparent'}` }}>
          <img src={im.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {im.badge && <span style={{ position: 'absolute', top: 4, right: 4, background: T.sun, color: '#1b1503', borderRadius: 6, padding: '1px 5px', font: `700 9.5px ${T.font}` }}>{im.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * A refusal from studioApi.refusal(), said plainly, with the way forward:
 * an upgrade or a top-up opens the website's plans (the World has no checkout
 * of its own for these), never a dead end.
 */
export function Refusal({ refusal, onDismiss }) {
  if (!refusal) return null;
  const tone = refusal.kind === 'error' ? 'error' : 'warn';
  // The website's plans page is /plans (a bare /pricing reads as an artist handle there).
  const link = refusal.kind === 'upgrade' || refusal.kind === 'credits' ? site('/plans') : null;
  return (
    <div style={{ border: `1px solid ${tone === 'error' ? 'rgba(255,122,122,0.35)' : 'rgba(255,196,107,0.35)'}`, borderRadius: 10, padding: '9px 11px', margin: '10px 0', background: 'rgba(255,255,255,0.03)' }}>
      {refusal.kind === 'upgrade' && refusal.title && <div style={{ color: T.ink, font: `700 13px ${T.font}`, marginBottom: 3 }}>{refusal.title}</div>}
      <div style={{ color: tone === 'error' ? T.bad : T.warn, font: `12.5px/1.45 ${T.font}` }}>{refusal.message}</div>
      <Row style={{ marginTop: 8 }}>
        {link && <a href={link} target="_blank" rel="noreferrer" style={{ color: T.sun, font: `700 12.5px ${T.font}` }}>{refusal.kind === 'credits' ? 'Top up credits ↗' : 'See plans ↗'}</a>}
        {onDismiss && <button type="button" onClick={onDismiss} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: T.faint, cursor: 'pointer', font: `12px ${T.font}` }}>Dismiss</button>}
      </Row>
    </div>
  );
}

/**
 * A wait, with its doors (platform rule: every wait needs a door — cancel,
 * retry, dismiss). The job keeps running on the server; leaving never loses it:
 * `walkAway` says where it will turn up.
 */
export function Waiting({ label, walkAway, onCancel, onDismiss }) {
  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: '11px 12px', margin: '10px 0', background: 'rgba(255,79,163,0.06)' }}>
      <Row gap={10} wrap={false}><Spinner /><div style={{ color: T.ink, font: `600 13.5px ${T.font}` }}>{label}</div></Row>
      {walkAway && <Note style={{ margin: '6px 0 0' }}>{walkAway}</Note>}
      {(onCancel || onDismiss) && (
        <Row style={{ marginTop: 8 }}>
          {onCancel && <Btn kind="ghost" onClick={onCancel}>Stop waiting</Btn>}
          {onDismiss && <Btn kind="ghost" onClick={onDismiss}>Hide</Btn>}
        </Row>
      )}
    </div>
  );
}

/** A file picker button (images only). `onFile(file)`. */
export function FilePick({ label = 'Choose image', onFile, accept = 'image/*', disabled }) {
  const ref = React.useRef(null);
  return (
    <>
      <Btn kind="ghost" disabled={disabled} onClick={() => ref.current?.click()}>{label}</Btn>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </>
  );
}

/** Stop pointer and key events reaching the 3D world while the studio is in use (WASD, drag-look). */
export const shield = {
  onPointerDown: (e) => e.stopPropagation(),
  onWheel: (e) => e.stopPropagation(),
  onKeyDown: (e) => e.stopPropagation(),
  onKeyUp: (e) => e.stopPropagation(),
};
